import React from "react"
import styled, {keyframes} from "styled-components"
import Layout from "../Layout";
import {Center, PageTitle} from "../shared/mix";
import ReactGA from "react-ga";
import SEO from "../SEO";

const Contact = () => {
    const handleClick = (e: React.MouseEvent) => {
        ReactGA.event({
            category: 'Contact-Click',
            //@ts-ignore
            action: e.target.href
        });
    }

    return (
        <Layout>
            <SEO
                title="Kontakt"
                pageType="ContactPage"
                description="Kontaktujte Matěje Půhoného — web developera z Pardubic. Email: info@puhony.eu, telefon: +420 703 992 854. LinkedIn, Messenger, Instagram."
                jsonLd={{
                    "@type": "ContactPage",
                    "@id": "https://puhony.eu/kontakt/#webpage",
                    "mainEntity": {
                        "@id": "https://puhony.eu/#person",
                        "@type": ["Person", "ProfessionalService"],
                        "name": "Matěj Půhoný",
                        "email": "info@puhony.eu",
                        "telephone": "+420703992854",
                        "contactPoint": [
                            {
                                "@type": "ContactPoint",
                                "contactType": "customer service",
                                "email": "info@puhony.eu",
                                "telephone": "+420703992854",
                                "availableLanguage": ["Czech", "English"],
                                "url": "https://puhony.eu/kontakt/"
                            }
                        ],
                        "sameAs": [
                            "https://www.linkedin.com/in/matej-puhony/",
                            "https://m.me/matej.puhony",
                            "https://instagram.com/matejpuhony"
                        ]
                    }
                }}
            />
            <Center>
                <PageTitle>Kontakt</PageTitle>
                <Subtitle>
                    Máte nápad na projekt, nebo potřebujete pomoct s&nbsp;něčím z&nbsp;digitálního světa?
                    Ozvěte se mi.
                </Subtitle>
            </Center>

            <Center>
                <PrimaryRow>
                    <PrimaryCard style={{animationDelay: '0s'}}>
                        <PrimaryIcon><i className="fa-solid fa-at" /></PrimaryIcon>
                        <PrimaryLabel>Email</PrimaryLabel>
                        <PrimaryLink href="mailto:info@puhony.eu" onClick={handleClick}>
                            info@puhony.eu
                        </PrimaryLink>
                    </PrimaryCard>
                    <PrimaryCard style={{animationDelay: '0.06s'}}>
                        <PrimaryIcon><i className="fa-solid fa-phone" /></PrimaryIcon>
                        <PrimaryLabel>Telefon</PrimaryLabel>
                        <PrimaryLink href="tel:703992854" onClick={handleClick}>
                            (+420) 703 992 854
                        </PrimaryLink>
                    </PrimaryCard>
                </PrimaryRow>

                <SectionLabel>Sociální sítě</SectionLabel>
                <SocialRow>
                    <SocialCard href="https://www.linkedin.com/in/matej-puhony/" target="_blank" rel="noopener noreferrer" onClick={handleClick} style={{animationDelay: '0.12s'}}>
                        <SocialIcon><i className="fa-brands fa-linkedin" /></SocialIcon>
                        <SocialInfo>
                            <SocialName>LinkedIn</SocialName>
                            <SocialHandle>/in/matej-puhony</SocialHandle>
                        </SocialInfo>
                        <SocialArrow><i className="fa-solid fa-arrow-up-right-from-square" /></SocialArrow>
                    </SocialCard>
                    <SocialCard href="https://m.me/matej.puhony" target="_blank" rel="noopener noreferrer" onClick={handleClick} style={{animationDelay: '0.16s'}}>
                        <SocialIcon><i className="fa-brands fa-facebook-messenger" /></SocialIcon>
                        <SocialInfo>
                            <SocialName>Messenger</SocialName>
                            <SocialHandle>matej.puhony</SocialHandle>
                        </SocialInfo>
                        <SocialArrow><i className="fa-solid fa-arrow-up-right-from-square" /></SocialArrow>
                    </SocialCard>
                    <SocialCard href="https://instagram.com/matejpuhony" target="_blank" rel="noopener noreferrer" onClick={handleClick} style={{animationDelay: '0.2s'}}>
                        <SocialIcon><i className="fa-brands fa-instagram" /></SocialIcon>
                        <SocialInfo>
                            <SocialName>Instagram</SocialName>
                            <SocialHandle>matejpuhony</SocialHandle>
                        </SocialInfo>
                        <SocialArrow><i className="fa-solid fa-arrow-up-right-from-square" /></SocialArrow>
                    </SocialCard>
                </SocialRow>

                <DarkSection style={{animationDelay: '0.24s'}}>
                    <DarkGrid>
                        <DarkBlock>
                            <DarkLabel>Živnostenský rejstřík</DarkLabel>
                            <DarkRow>
                                <DarkKey>IČO</DarkKey>
                                <DarkValue>08265771</DarkValue>
                            </DarkRow>
                            <DarkNote>Fyzická osoba podnikající dle živnostenského zákona</DarkNote>
                        </DarkBlock>
                        <DarkDivider />
                        <DarkBlock>
                            <DarkLabel>Platební údaje</DarkLabel>
                            <DarkNote>
                                <i className="fa-solid fa-circle-info" style={{marginRight: '0.5em', opacity: 0.5}} />
                                Informace k platbě jsou vždy uvedené na faktuře.
                            </DarkNote>
                        </DarkBlock>
                    </DarkGrid>
                </DarkSection>
            </Center>
        </Layout>
    )
}

export default Contact

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

/* ---- subtitle ---- */

const Subtitle = styled.p`
  text-align: center;
  max-width: 30em;
  margin: -1.5em auto 4em;
  font-size: 1.15em;
  line-height: 1.7;
  opacity: 0.55;
  font-weight: 300;
`

/* ---- primary contact cards ---- */

const PrimaryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5em;
  margin-bottom: 4em;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    gap: 1em;
  }
`

const PrimaryCard = styled.div`
  background: #f7f7f7;
  border: 1px solid #ebebeb;
  border-radius: 0.6em;
  padding: 2.2em 2em;
  text-align: center;
  animation: ${fadeUp} 0.5s ease both;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  }
`

const PrimaryIcon = styled.div`
  font-size: 1.4em;
  opacity: 0.3;
  margin-bottom: 0.8em;
`

const PrimaryLabel = styled.div`
  font-size: 0.75em;
  font-weight: 400;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.35;
  margin-bottom: 0.6em;
`

const PrimaryLink = styled.a`
  display: block;
  font-size: 1.2em;
  font-weight: 700;
  color: rgba(25, 25, 25, 1);
  text-decoration: none;
  letter-spacing: -0.01em;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.65;
  }

  @media (max-width: 400px) {
    font-size: 1em;
  }
`

/* ---- social cards ---- */

const SectionLabel = styled.div`
  font-size: 0.75em;
  font-weight: 400;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.3;
  margin-bottom: 1.5em;
`

const SocialRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8em;
  margin-bottom: 4em;
`

const SocialArrow = styled.span`
  font-size: 0.8em;
  opacity: 0;
  transition: opacity 0.2s ease, transform 0.2s ease;
  margin-left: auto;
  color: rgba(25, 25, 25, 0.4);
`

const SocialCard = styled.a`
  display: flex;
  align-items: center;
  gap: 1.2em;
  padding: 1.2em 1.5em;
  background: #f7f7f7;
  border: 1px solid #ebebeb;
  border-radius: 0.5em;
  text-decoration: none;
  color: inherit;
  animation: ${fadeUp} 0.5s ease both;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateX(4px);
    border-color: #d0d0d0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);

    ${SocialArrow} {
      opacity: 1;
      transform: translateX(2px);
    }
  }
`

const SocialIcon = styled.div`
  font-size: 1.3em;
  width: 1.6em;
  text-align: center;
  opacity: 0.6;
`

const SocialInfo = styled.div``

const SocialName = styled.div`
  font-weight: 700;
  font-size: 0.95em;
  letter-spacing: -0.01em;
`

const SocialHandle = styled.div`
  font-size: 0.8em;
  opacity: 0.4;
  font-weight: 300;
  margin-top: 0.15em;
`

/* ---- dark business info section ---- */

const DarkSection = styled.div`
  background: rgba(25, 25, 25, 1);
  color: #fff;
  border-radius: 0.6em;
  padding: 2.5em 2.5em;
  margin-bottom: 6em;
  animation: ${fadeUp} 0.5s ease both;

  @media (max-width: 560px) {
    padding: 2em 1.5em;
  }
`

const DarkGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 2em;
  align-items: start;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 2em;
  }
`

const DarkDivider = styled.div`
  width: 1px;
  align-self: stretch;
  background: rgba(255, 255, 255, 0.1);

  @media (max-width: 600px) {
    width: 100%;
    height: 1px;
  }
`

const DarkBlock = styled.div``

const DarkLabel = styled.div`
  font-size: 0.75em;
  font-weight: 400;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.35;
  margin-bottom: 1.2em;
`

const DarkRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.8em;
  margin-bottom: 0.8em;
`

const DarkKey = styled.span`
  font-size: 0.72em;
  font-weight: 700;
  letter-spacing: 0.08em;
  opacity: 0.45;
  background: rgba(255, 255, 255, 0.08);
  padding: 0.25em 0.6em;
  border-radius: 0.25em;
`

const DarkValue = styled.span`
  font-size: 1.1em;
  font-weight: 700;
  letter-spacing: 0.03em;
`

const DarkNote = styled.div`
  font-size: 0.85em;
  line-height: 1.6;
  opacity: 0.4;
  font-weight: 300;
`
